<template>
    <div>
        <div class="kgr-row">
            {{ $t('offering.options.category.intro') }}
        </div>
        <kgr-form-scaffold
            :with-back-button="false"
            :pending="pending"
            @primaryBtnClicked="save"
        >
            <kgr-loader v-if="loading" />
            <table
                v-else
                class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell--headline"></th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('offering.options.category') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('offering.options.category.headline.edit') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(category, index) in offeringCategories"
                        :key="index"
                        class="kgr-table__row"
                    >
                        <td :style="'background-color:' + category.backgroundColor + '; width: 6px;'"></td>

                        <td
                            v-if="isMarkEdited(category)"
                            class="kgr-table__cell"
                        >
                            <div class="kgr-inputGroup kgr-postfix kgr-postfix--top">
                                <div class="kgr-postfix__fixed">
                                    <input
                                        v-model="category.backgroundColor"
                                        type="color"
                                        class="kgr-input"
                                        style="width: 40px;"
                                    />
                                </div>
                                <input
                                    v-if="category.isDefault"
                                    type="text"
                                    :value="$t(category.name)"
                                    maxlength="255"
                                    class="kgr-input"
                                    :disabled="true"
                                />
                                <input
                                    v-else
                                    v-model="category.name"
                                    type="text"
                                    maxlength="255"
                                    class="kgr-input"
                                    placeholder="Name"
                                />
                            </div>
                            <div class="kgr-inputGroup__text kgr-inputGroup__text--error">
                                {{ showErrorMessage(index) }}
                            </div>
                        </td>
                        <td
                            v-else
                            class="kgr-table__cell"
                        >
                            <span v-if="category.isDefault">{{ $t(category.name) }} <span class="kgr-color kgr-color--gray">({{ $t('offering.system.default') }})</span></span>
                            <span
                                v-else
                                :class="category.isArchived ? 'kgr-color kgr-color--gray' : ''"
                            >{{ category.name }}</span>
                        </td>
                        <td
                            class="kgr-table__cell"
                            style="vertical-align: top;"
                        >
                            <span
                                class="kgr-hidden kgr-hidden--fadeInOnUnhidden"
                                :class="{ 'is-unhidden': !isMarked(category) }"
                            >
                                <button
                                    v-if="category.id"
                                    class="kgr-table__action"
                                    :title="$t('offering.options.category.edit')"
                                    @click="markEdited(category)"
                                >
                                    <kgr-icon-edit
                                        size="15"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- delete button active -->
                                <button
                                    v-if="category.isDeletable"
                                    class="kgr-table__action"
                                    :title="category.id ? $t('offering.options.category.delete') : null"
                                    @click="category.id ? markForDeletion(category) : removeOfferingCategory(category)"
                                >
                                    <kgr-icon-delete style="pointer-events: none" />
                                </button>

                                <!-- delete button disabled -->
                                <button
                                    v-if="!category.isDeletable && !category.isDefault"
                                    class="kgr-table__action"
                                    :title="category.reasonForNotDeletable"
                                >
                                    <kgr-icon-delete
                                        class="kgr-icon--light"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- archive button active -->
                                <button
                                    v-if="category.id && category.isArchivable"
                                    class="kgr-table__action"
                                    :title="$t('offering.options.category.archive')"
                                    @click="markForArchive(category)"
                                >
                                    <kgr-icon
                                        icon-class="archive"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- archive button disabled -->
                                <button
                                    v-if="category.id && !category.isArchivable && !category.isDefault"
                                    class="kgr-table__action"
                                    :title="category.reasonForNotArchivable"
                                >
                                    <kgr-icon
                                        icon-class="archive"
                                        class="kgr-icon--light"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- unarchive button active -->
                                <button
                                    v-if="category.id && category.isArchived"
                                    class="kgr-table__action"
                                    :title="$t('offering.options.category.unarchive')"
                                    @click="markForUnarchive(category)"
                                >
                                    <kgr-icon
                                        icon-class="unarchive"
                                        style="pointer-events: none"
                                    />
                                </button>

                                <!-- unarchive button disabled -->
                                <button
                                    v-if="category.id && !category.isArchived && !category.isDefault"
                                    class="kgr-table__action"
                                    :title="$t('offering.options.category.notArchived')"
                                >
                                    <kgr-icon
                                        icon-class="unarchive"
                                        class="kgr-icon--light"
                                        style="pointer-events: none"
                                    />
                                </button>
                            </span>
                            <a
                                v-if="isMarkedForDeletion(category)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForDeletion(category)"
                            >{{ $t('offering.options.category.undoDeletion') }}</a>
                            <a
                                v-if="isMarkedForArchive(category)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForArchive(category)"
                            >{{ $t('offering.options.category.undoArchive') }}</a>
                            <a
                                v-if="isMarkedForUnarchive(category)"
                                class="undoRemoveRowIcon kgr-link kgr-link--unobtrusive"
                                @click="unmarkForUnarchive(category)"
                            >{{ $t('offering.options.category.undoUnarchive') }}</a>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="kgr-text kgr-text--rightAligned">
                <div
                    class="kgr-button kgr-button--accent"
                    @click="addOfferingCategory()"
                >
                    {{ $t('offering.options.category.add_new') }}
                </div>
            </div>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import Error from '../../js/model/Error';
import FormScaffold from '../../components/common/form/FormScaffold';
import Icon from '../../components/common/icon/Icon';
import IconDelete from '../../components/common/icon/IconDelete';
import IconEdit from '../../components/common/icon/IconEdit';
import Loading from '../../components/common/Loading';
import OfferingCategory from '../../js/dto/offering/OfferingCategory';
import OfferingCategoryRepository from '../../js/repository/club/offering/OfferingCategoryRepository';

export default {
    name: 'OfferingCategoryOverview',
    components: {
        'kgr-loader': Loading,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-icon': Icon,
        'kgr-form-scaffold': FormScaffold,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            pending: false,
            offeringCategories: [],
            markedForDeletion: [],
            markedForArchive: [],
            markedForUnarchive: [],
            edited: [],
            error: new Error(),
        };
    },
    mounted() {
        this.load();
    },
    methods: {
        load(loading = true) {
            this.loading = loading;
            OfferingCategoryRepository.findAllWithDefaultsAndArchived(this.clubId)
                .then(categories => this.offeringCategories = categories)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        save() {
            this.pending = true;
            OfferingCategoryRepository.bulkUpdate(this.clubId, this.offeringCategories)
                .then(() => {
                    this.load(false);
                    this.$notify.success('saveChanges.success');
                })
                .catch(error => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.pending = false;
                });
        },
        addOfferingCategory() {
            const category = OfferingCategory.create();
            this.offeringCategories.push(category);
            this.markEdited(category);
        },
        removeOfferingCategory(category) {
            this.offeringCategories = this.offeringCategories.filter(element => element !== category);
        },
        markForDeletion(category) {
            this.markedForDeletion.push(category);
            category.delete = true;
            category.isMarked = true;
        },
        unmarkForDeletion(category) {
            this.markedForDeletion = this.markedForDeletion.filter(item => item !== category);
            category.delete = false;
            category.isMarked = false;
        },
        markForArchive(category) {
            this.markedForArchive.push(category);
            category.isArchived = true;
            category.isMarked = true;
        },
        unmarkForArchive(category) {
            this.markedForArchive = this.markedForArchive.filter(item => item !== category);
            category.isArchived = false;
            category.isMarked = false;
        },
        markForUnarchive(category) {
            this.markedForUnarchive.push(category);
            category.isArchived = false;
            category.isMarked = true;
        },
        unmarkForUnarchive(category) {
            this.markedForUnarchive = this.markedForUnarchive.filter(item => item !== category);
            category.isArchived = true;
            category.isMarked = false;
        },
        markEdited(category) {
            this.edited.push(category);
        },
        isMarkEdited(category) {
            return this.edited.indexOf(category) !== -1;
        },
        isMarkedForDeletion(category) {
            return this.markedForDeletion.indexOf(category) !== -1;
        },
        isMarkedForArchive(category) {
            return this.markedForArchive.indexOf(category) !== -1;
        },
        isMarkedForUnarchive(category) {
            return this.markedForUnarchive.indexOf(category) !== -1;
        },
        isMarked(category) {
            return this.isMarkedForDeletion(category) ||
                this.isMarkedForArchive(category) ||
                this.isMarkedForUnarchive(category);
        },
        showErrorMessage(index) {
            return this.error.messageForField('offeringCategories[' + index + '][name]');
        }
    }
}
</script>
